import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "@/components/ui/dialog";
import { useToast } from "@/hooks/useToast";
import { useAuthStore } from "@/store";
import { Loader2, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";

export function FeedbackDialog({ open, onOpenChange }) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { profile } = useAuthStore();
  const { toast } = useToast();
  const messageRef = useRef(null);

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    category: "General Feedback",
    message: "",
  });

  // Update form data when profile changes or dialog opens
  useEffect(() => {
    if (open && profile) {
      setFormData((prev) => ({
        ...prev,
        name: profile.full_name || profile.username || "",
        email: profile.email || "",
        message: "",
      }));

      // Focus message field after a short delay
      const timer = setTimeout(() => {
        messageRef.current?.focus();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [open, profile]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleClose = () => {
    if (isSubmitting) return;
    onOpenChange(false);
  };

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();

    if (!formData.message || formData.message.trim().length < 10) {
      toast({
        title: "Message too short",
        description: "Please provide at least 10 characters of feedback.",
        variant: "destructive",
      });
      return;
    }

    try {
      setIsSubmitting(true);

      const response = await fetch(
        `${import.meta.env.VITE_API_BASE_URL}/api/notifications/slack/feedback`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(formData),
        }
      );

      const result = await response.json();

      if (result.success) {
        toast({
          title: "Feedback Sent",
          description: "Thank you for your feedback! We appreciate your input.",
        });

        // Reset and close
        setFormData((prev) => ({ ...prev, message: "" }));
        onOpenChange(false);
      } else {
        throw new Error(result.error || "Failed to send feedback");
      }
    } catch (error) {
      console.error("Failed to send feedback:", error);
      toast({
        title: "Submission Failed",
        description:
          error.message ||
          "There was an error sending your feedback. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleKeyDown = (e) => {
    if (
      e.key === "Enter" &&
      e.ctrlKey &&
      formData.message.trim() &&
      !isSubmitting
    ) {
      e.preventDefault();
      handleSubmit();
    } else if (e.key === "Escape" && !isSubmitting) {
      e.preventDefault();
      handleClose();
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-[90vw] max-h-[90vh] w-[500px] h-auto bg-white dark:bg-zinc-900 border-gray-200 dark:border-[#3a3a3a] text-gray-900 dark:text-white p-0 gap-0 rounded-2xl flex flex-col overflow-hidden shadow-2xl"
        hideCloseButton={true}
        onEscapeKeyDown={(e) => isSubmitting && e.preventDefault()}
        onPointerDownOutside={(e) => isSubmitting && e.preventDefault()}
      >
        <DialogTitle className="sr-only">Send Feedback</DialogTitle>
        <DialogDescription className="sr-only">
          Send feedback about your experience with ArtifactChat
        </DialogDescription>

        {/* Header */}
        <div className="flex items-center justify-between py-3 px-4 bg-white dark:bg-zinc-900 flex-shrink-0 border-b border-gray-100 dark:border-white/5">
          <h2 className="text-lg font-medium text-gray-900 dark:text-white">
            Send Feedback
          </h2>
          <button
            onClick={handleClose}
            disabled={isSubmitting}
            className="p-1.5 rounded-full hover:bg-gray-100 dark:hover:bg-zinc-800 text-gray-500 dark:text-zinc-400 hover:text-gray-900 dark:hover:text-white transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 flex flex-col overflow-hidden bg-white dark:bg-zinc-900">
          <div className="flex-1 overflow-auto px-4 py-4 space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <label
                  htmlFor="feedback-name"
                  className="text-sm font-medium text-gray-700 dark:text-gray-300"
                >
                  Name
                </label>
                <input
                  id="feedback-name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Your name"
                  disabled={isSubmitting}
                  className="h-9 w-full rounded-lg border border-gray-300 dark:border-zinc-700 bg-white dark:bg-zinc-800 px-3 py-2 text-sm text-gray-900 dark:text-zinc-100 focus:outline-none focus:ring-2 focus:ring-purple-400/50 transition-all"
                />
              </div>
              <div className="space-y-1.5">
                <label
                  htmlFor="feedback-email"
                  className="text-sm font-medium text-gray-700 dark:text-gray-300"
                >
                  Email
                </label>
                <input
                  id="feedback-email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="your@email.com"
                  disabled={isSubmitting}
                  className="h-9 w-full rounded-lg border border-gray-300 dark:border-zinc-700 bg-white dark:bg-zinc-800 px-3 py-2 text-sm text-gray-900 dark:text-zinc-100 focus:outline-none focus:ring-2 focus:ring-purple-400/50 transition-all"
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <label
                htmlFor="feedback-category"
                className="text-sm font-medium text-gray-700 dark:text-gray-300"
              >
                Category
              </label>
              <div className="relative">
                <select
                  id="feedback-category"
                  name="category"
                  value={formData.category}
                  onChange={handleChange}
                  disabled={isSubmitting}
                  className="h-9 w-full px-3 bg-white dark:bg-zinc-800 border border-gray-300 dark:border-zinc-700 rounded-lg text-sm text-gray-900 dark:text-white appearance-none focus:outline-none focus:ring-2 focus:ring-purple-400/50 transition-all cursor-pointer"
                >
                  <option value="General Feedback">General Feedback</option>
                  <option value="Feature Request">Feature Request</option>
                  <option value="UI/UX Suggestion">UI/UX Suggestion</option>
                  <option value="Performance">Performance</option>
                  <option value="Other">Other</option>
                </select>
                <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-gray-400">
                  <svg
                    width="10"
                    height="10"
                    viewBox="0 0 12 12"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M2.5 4.5L6 8L9.5 4.5"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
              </div>
            </div>

            <div className="space-y-1.5">
              <label
                htmlFor="feedback-message"
                className="text-sm font-medium text-gray-700 dark:text-gray-300"
              >
                Message
              </label>
              <textarea
                ref={messageRef}
                id="feedback-message"
                name="message"
                value={formData.message}
                onChange={handleChange}
                onKeyDown={handleKeyDown}
                placeholder="Tell us about your experience, suggestions for improvement, or any other feedback..."
                disabled={isSubmitting}
                rows={4}
                className="w-full resize-none rounded-lg border border-gray-300 dark:border-zinc-700 bg-white dark:bg-zinc-800 px-3 py-2 text-sm text-gray-900 dark:text-zinc-100 focus:outline-none focus:ring-2 focus:ring-purple-400/50 transition-all"
              />
            </div>

            {/* Info Box */}
            <div className="flex items-start gap-3 rounded-xl bg-gray-50 dark:bg-zinc-800/50 p-3 border border-gray-100 dark:border-white/5">
              <div className="text-amber-500 dark:text-amber-400 mt-0.5">
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path d="M14.3352 10.0257C14.3352 7.6143 12.391 5.66554 10.0002 5.66537C7.60929 5.66537 5.66528 7.61419 5.66528 10.0257C5.66531 11.5493 6.44221 12.8881 7.61938 13.6683H12.3811C13.558 12.8881 14.3352 11.5491 14.3352 10.0257ZM8.84399 16.9984C9.07459 17.3983 9.50543 17.6683 10.0002 17.6683C10.495 17.6682 10.926 17.3984 11.1565 16.9984H8.84399ZM8.08813 15.6683H11.9114V14.9984H8.08813V15.6683ZM1.66626 9.33529L1.80103 9.34896C2.10381 9.41116 2.3313 9.67914 2.3313 10.0003C2.33115 10.3214 2.10377 10.5896 1.80103 10.6517L1.66626 10.6654H0.833252C0.466091 10.6654 0.168389 10.3674 0.168213 10.0003C0.168213 9.63306 0.465983 9.33529 0.833252 9.33529H1.66626ZM19.1663 9.33529L19.301 9.34896C19.6038 9.41116 19.8313 9.67914 19.8313 10.0003C19.8311 10.3214 19.6038 10.5896 19.301 10.6517L19.1663 10.6654H18.3333C17.9661 10.6654 17.6684 10.3674 17.6682 10.0003C17.6682 9.63306 17.966 9.33529 18.3333 9.33529H19.1663ZM3.0481 3.04818C3.2753 2.82099 3.62593 2.79189 3.88403 2.96224L3.98853 3.04818L4.57739 3.63705L4.66235 3.74154C4.83285 3.99966 4.80464 4.35021 4.57739 4.57748C4.35013 4.80474 3.99958 4.83293 3.74146 4.66244L3.63696 4.57748L3.0481 3.98861L2.96216 3.88412C2.79181 3.62601 2.82089 3.27538 3.0481 3.04818ZM16.012 3.04818C16.2717 2.7886 16.6927 2.78852 16.9524 3.04818C17.2117 3.30786 17.2119 3.72901 16.9524 3.98861L16.3625 4.57748C16.1028 4.83717 15.6818 4.83718 15.4221 4.57748C15.1626 4.31776 15.1625 3.89669 15.4221 3.63705L16.012 3.04818ZM9.33521 1.66634V0.833336C9.33521 0.466067 9.63297 0.168297 10.0002 0.168297C10.3674 0.168472 10.6653 0.466175 10.6653 0.833336V1.66634C10.6653 2.0335 10.3674 2.33121 10.0002 2.33138C9.63297 2.33138 9.33521 2.03361 9.33521 1.66634ZM15.6653 10.0257C15.6653 11.9571 14.7058 13.6634 13.2415 14.6917V16.3333C13.2415 16.7004 12.9444 16.9971 12.5774 16.9974C12.282 18.1473 11.2423 18.9982 10.0002 18.9984C8.75792 18.9984 7.71646 18.1476 7.42114 16.9974C7.05476 16.9964 6.75806 16.7 6.75806 16.3333V14.6917C5.29383 13.6634 4.33523 11.957 4.33521 10.0257C4.33521 6.88608 6.86835 4.33529 10.0002 4.33529C13.132 4.33547 15.6653 6.88618 15.6653 10.0257Z" />
                </svg>
              </div>
              <div>
                <strong className="text-gray-700 dark:text-gray-200 block text-xs font-semibold">
                  Your feedback matters
                </strong>
                <p className="text-gray-500 dark:text-gray-400 text-[11px] leading-relaxed">
                  Help us improve ArtifactChat by sharing your thoughts and
                  suggestions.
                </p>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between px-4 py-3 bg-gray-50/50 dark:bg-zinc-900/50 border-t border-gray-100 dark:border-white/5 flex-shrink-0">
            <Button
              type="button"
              variant="ghost"
              onClick={handleClose}
              disabled={isSubmitting}
              className="text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-zinc-800 px-3 py-2 rounded-lg text-sm font-medium"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={isSubmitting || !formData.message.trim()}
              className="bg-gray-900 dark:bg-white hover:bg-gray-800 dark:hover:bg-gray-100 text-white dark:text-gray-900 px-4 rounded-full text-sm font-semibold h-9 shadow-sm transition-all"
            >
              {isSubmitting ? (
                <div className="flex items-center gap-2">
                  <span>Sending...</span>
                  <Loader2 className="h-4 w-4 animate-spin" />
                </div>
              ) : (
                "Send Feedback"
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default FeedbackDialog;
